import { css } from 'styled-components';

export type MarkdownTextSize = 'body2' | 'body3';

const textSizeToTokenMap = {
  body2: '--wui-typography-body-2-size',
  body3: '--wui-typography-body-3-size',
} as const;

export const baseMarkdownCss = (textSize: MarkdownTextSize = 'body2') => css`
  --wui-markdown-base-unit: var(${textSizeToTokenMap[textSize]});

  font-size: var(--wui-markdown-base-unit);

  & > * {
    margin: 0;
    margin-block-start: calc(var(--wui-markdown-base-unit) * 0.75);
  }

  & > *:first-child {
    margin-block-start: 0;
  }

  :is(h1, h2, h3, h4, h5, h6) {
    margin-block-start: calc(var(--wui-markdown-base-unit) * 1.5);
  }

  h1 {
    font-size: calc(var(--wui-markdown-base-unit) * 2);
  }

  h2 {
    font-size: calc(var(--wui-markdown-base-unit) * 1.5);
  }

  h3 {
    font-size: calc(var(--wui-markdown-base-unit) * 1.25);
  }

  h4 {
    font-size: calc(var(--wui-markdown-base-unit) * 1);
  }

  h5 {
    font-size: calc(var(--wui-markdown-base-unit) * 0.875);
  }

  h6 {
    font-size: calc(var(--wui-markdown-base-unit) * 0.75);
  }

  p {
    font-size: var(--wui-markdown-base-unit);
    line-height: 1.5;
    font-weight: var(--wui-typography-weight-body);
  }

  a {
    color: var(--wui-color-text-link);
    text-decoration: underline;
  }

  strong {
    font-weight: var(--wui-typography-weight-body-bold);
  }

  code {
    background-color: var(--wui-color-bg-surface-secondary);
    border-radius: var(--wui-border-radius-01);
    font-size: 0.9em;
    font-family: var(--wui-typography-family-mono);
    border: 1px solid var(--wui-color-border);
    padding: 2px 4px;
    margin: 0 2px;
  }

  ul,
  ol {
    margin-inline-start: 1.5em;
    padding: 0;
  }

  li {
    margin-block-start: 0.25em;
  }

  blockquote {
    margin-inline: 0;
    border-inline-start: 3px solid var(--wui-color-border-secondary);
    padding-inline-start: 1em;
    padding-block: 0.125em;

    & > *:first-child {
      margin-block-start: 0;
    }

    & > *:last-child {
      margin-block-end: 0;
    }
  }

  pre {
    background-color: var(--wui-color-bg-surface-secondary);
    border-radius: var(--wui-border-radius-01);
    padding: 00.75em;
    overflow-x: auto;
  }

  pre code {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
  }

  hr {
    border: none;
    border-top: 1px solid var(--wui-color-border);
    margin-block: calc(var(--wui-markdown-base-unit) * 1.5);
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;
